import z from "zod";

const sunrunApiErrorTypeValues = [
  "FAILED_TO_ACCEPT_WORK_ORDER",
  "FAILED_TO_DECLINE_WORK_ORDER",
  "FAILED_TO_DECLINE_PARTNER_TASK",
  "FAILED_TO_REQUEST_REDESIGN_WORK_ORDER",
  "FAILED_TO_RESCHEDULE_WORK_ORDER",
  "FAILED_TO_SCHEDULE_WORK_ORDER",
  "FAILED_TO_UPDATE_STATUS_TO_HIC_SOLD_PENDING_SCHEDULING_WORK_ORDER",
  "FAILED_TO_SUBMIT_QUOTE",
  "FAILED_TO_SUBMIT_WORK_ORDER_INVOICE",
  "FAILED_TO_UPLOAD_PHOTOS",
  "FAILED_TO_UPLOAD_NON_PHOTO_FILES",
  "FAILED_TO_UPLOAD_WORK_ORDER_NOTES",
  "FAILED_TO_CREATE_NOTE",
  "FAILED_TO_CREATE_ATTACHMENT",
  "UNABLE_TO_FIND_PARTNER_TASK_RECORD_ID",
  "FIC_API_RESPONSE_PARSE_FAILED",
  "SUNRUN_REDESIGN_SEND_EXHAUSTED",
  "SUNRUN_APEX_JOB_CAP_EXCEEDED",
  "SUNRUN_APEX_HEAP_EXCEEDED",
  "PHOTO_COMPRESSION_UNAVAILABLE_ON_RUNTIME",
  "INVALID_WORK_ORDER_STATUS",
  "SUNRUN_APEX_LIMIT_EXCEPTION",
] as const;

const sunrunApiErrorTypeSchema = z.enum(sunrunApiErrorTypeValues);
const SunrunApiErrorType = sunrunApiErrorTypeSchema.enum;
type SunrunApiErrorType = z.infer<typeof sunrunApiErrorTypeSchema>;

export type SunrunApiErrors =
  | {
      errorType: typeof SunrunApiErrorType.FAILED_TO_ACCEPT_WORK_ORDER;
      data: { workOrderRecordId: string; reason: string | null | undefined };
    }
  | {
      errorType: typeof SunrunApiErrorType.FAILED_TO_DECLINE_WORK_ORDER;
      data: { workOrderRecordId: string; reason: string | null | undefined };
    }
  | {
      errorType: typeof SunrunApiErrorType.FAILED_TO_DECLINE_PARTNER_TASK;
      data: { partnerTaskRecordId: string; reason: string | null | undefined };
    }
  | {
      errorType: typeof SunrunApiErrorType.FAILED_TO_REQUEST_REDESIGN_WORK_ORDER;
      data: { workOrderRecordId: string; reason: string | null | undefined; parseError?: unknown };
    }
  | {
      errorType: typeof SunrunApiErrorType.FAILED_TO_RESCHEDULE_WORK_ORDER;
      data: { workOrderRecordId: string; reason: string | null | undefined };
    }
  | {
      errorType: typeof SunrunApiErrorType.FAILED_TO_SCHEDULE_WORK_ORDER;
      data: {
        workOrderRecordId: string;
        reason: string | null | undefined;
        parseError?: unknown;
        httpStatus?: number;
        responseBody?: string;
      };
    }
  | {
      errorType: typeof SunrunApiErrorType.FAILED_TO_UPDATE_STATUS_TO_HIC_SOLD_PENDING_SCHEDULING_WORK_ORDER;
      data: { workOrderRecordId: string; reason: string | null | undefined; parseError?: unknown };
    }
  | {
      errorType: typeof SunrunApiErrorType.FAILED_TO_SUBMIT_QUOTE;
      data: { partnerTaskRecordId: string; reason: string | null | undefined; parseError?: unknown };
    }
  | {
      errorType: typeof SunrunApiErrorType.FAILED_TO_SUBMIT_WORK_ORDER_INVOICE;
      data: { workOrderRecordId: string; reason: string | null | undefined; parseError?: unknown };
    }
  | {
      errorType: typeof SunrunApiErrorType.FAILED_TO_UPLOAD_PHOTOS;
      data: { parentId: string; reason: string; parseError?: unknown };
    }
  | {
      errorType: typeof SunrunApiErrorType.FAILED_TO_UPLOAD_NON_PHOTO_FILES;
      data: { parentRecordId: string; proofType: string; reason: string | null | undefined; parseError?: unknown };
    }
  | {
      errorType: typeof SunrunApiErrorType.FAILED_TO_UPLOAD_WORK_ORDER_NOTES;
      data: { workOrderRecordId: string; reason: string | null | undefined; parseError?: unknown; errors?: unknown };
    }
  | {
      errorType: typeof SunrunApiErrorType.FAILED_TO_CREATE_NOTE;
      data: { parentId: string; reason: string; parseError?: unknown; errors?: unknown };
    }
  | {
      errorType: typeof SunrunApiErrorType.FAILED_TO_CREATE_ATTACHMENT;
      data: { parentId: string; reason: string; parseError?: unknown; errors?: unknown };
    }
  | {
      errorType: typeof SunrunApiErrorType.UNABLE_TO_FIND_PARTNER_TASK_RECORD_ID;
      data: { bidRequestId: string; externalUrl?: string | null };
    }
  | {
      errorType: typeof SunrunApiErrorType.FIC_API_RESPONSE_PARSE_FAILED;
      data: { operation: string };
    }
  | {
      errorType: typeof SunrunApiErrorType.SUNRUN_REDESIGN_SEND_EXHAUSTED;
      data: { projectId: number; taskId: string };
    }
  | {
      errorType: typeof SunrunApiErrorType.SUNRUN_APEX_JOB_CAP_EXCEEDED;
      data: { requestPath: string };
    }
  | {
      errorType: typeof SunrunApiErrorType.SUNRUN_APEX_HEAP_EXCEEDED;
      data: { requestPath: string };
    }
  | {
      errorType: typeof SunrunApiErrorType.PHOTO_COMPRESSION_UNAVAILABLE_ON_RUNTIME;
      data: { fileId: string; reason: string };
    }
  | {
      errorType: typeof SunrunApiErrorType.INVALID_WORK_ORDER_STATUS;
      data: { workOrderNumber: string; status: string };
    }
  | {
      errorType: typeof SunrunApiErrorType.SUNRUN_APEX_LIMIT_EXCEPTION;
      data: { requestPath: string };
    };

export const sunrunApiErrorDetails: Record<SunrunApiErrorType, { sentryDescription: string }> = {
  FAILED_TO_ACCEPT_WORK_ORDER: { sentryDescription: "Failed to accept work order" },
  FAILED_TO_DECLINE_WORK_ORDER: { sentryDescription: "Failed to decline work order" },
  FAILED_TO_DECLINE_PARTNER_TASK: { sentryDescription: "Failed to decline partner task" },
  FAILED_TO_REQUEST_REDESIGN_WORK_ORDER: { sentryDescription: "Failed to request redesign work order" },
  FAILED_TO_RESCHEDULE_WORK_ORDER: { sentryDescription: "Failed to reschedule work order" },
  FAILED_TO_SCHEDULE_WORK_ORDER: { sentryDescription: "Failed to schedule work order" },
  FAILED_TO_UPDATE_STATUS_TO_HIC_SOLD_PENDING_SCHEDULING_WORK_ORDER: {
    sentryDescription: "Failed to update status to HIC Sold/Pending Scheduling work order",
  },
  FAILED_TO_SUBMIT_QUOTE: { sentryDescription: "Failed to submit quote" },
  FAILED_TO_SUBMIT_WORK_ORDER_INVOICE: { sentryDescription: "Failed to submit work order invoice" },
  FAILED_TO_UPLOAD_PHOTOS: { sentryDescription: "Failed to upload photos" },
  FAILED_TO_UPLOAD_NON_PHOTO_FILES: { sentryDescription: "Failed to upload non photo files" },
  FAILED_TO_UPLOAD_WORK_ORDER_NOTES: { sentryDescription: "Failed to upload work order notes" },
  FAILED_TO_CREATE_NOTE: { sentryDescription: "Failed to create Salesforce Note" },
  FAILED_TO_CREATE_ATTACHMENT: { sentryDescription: "Failed to create Salesforce Attachment" },
  UNABLE_TO_FIND_PARTNER_TASK_RECORD_ID: {
    sentryDescription: "Unable to find partner task record ID for API submission",
  },
  FIC_API_RESPONSE_PARSE_FAILED: { sentryDescription: "Sunrun FIC API response failed schema validation" },
  SUNRUN_REDESIGN_SEND_EXHAUSTED: {
    sentryDescription:
      "Durable Sunrun redesign send exhausted its retries — chunked upload or work-order request never succeeded",
  },
  SUNRUN_APEX_JOB_CAP_EXCEEDED: {
    sentryDescription: "Sunrun's Salesforce org hit its 125 scheduled Apex job cap, so the request could not be served",
  },
  SUNRUN_APEX_HEAP_EXCEEDED: {
    sentryDescription: "The request body overflowed Salesforce's synchronous Apex heap, so Sunrun rejected the upload",
  },
  PHOTO_COMPRESSION_UNAVAILABLE_ON_RUNTIME: {
    sentryDescription:
      "sharp failed to load — Sunrun photo compression only runs in the sunrun-automations container, which ships sharp's native binaries",
  },
  INVALID_WORK_ORDER_STATUS: { sentryDescription: "Invalid work order status received from Sunrun" },
  SUNRUN_APEX_LIMIT_EXCEPTION: {
    sentryDescription:
      "Sunrun's photo upload endpoint failed with an Apex LimitException that repeats on every retry, so the photos must be uploaded manually",
  },
};
